/**
 * Las reglas de leer un código de barras con la cámara.
 *
 * ═══════════════════════════════════════════════════════════════════════════════
 *  POR QUÉ ESTO ESTÁ APARTE DE LA PANTALLA
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * Porque la cámara no se puede probar y estas reglas sí. Un navegador de mentira
 * no tiene cámara, así que la pantalla del escáner solo se puede mirar a ojo;
 * pero decidir **cuándo una lectura vale y cuándo hay que ignorarla** es lógica
 * pura, y ahí es donde están los fallos que no se ven.
 *
 * ═══════════════════════════════════════════════════════════════════════════════
 *  EL PROBLEMA QUE NADIE ESPERA: LA CÁMARA LEE UNA Y OTRA VEZ
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * Un lector láser dispara una vez por producto: se pasa, pita, se acabó. Una
 * cámara no: mientras el código esté delante lo lee **una y otra vez**, cada
 * `INTERVALO_DE_LECTURA`.
 *
 * Sin freno, apuntar dos segundos a una harina mete diez harinas al carrito.
 * Y el cajero no lo ve venir, porque en su cabeza «pasó el producto una vez».
 *
 * ═══════════════════════════════════════════════════════════════════════════════
 *  ⚠️ EL MISMO CÓDIGO CUENTA OTRA VEZ CUANDO SALIÓ DE LA CÁMARA, NO AL RATO
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * Antes la regla era «el mismo código, dos segundos después de aceptarlo». El
 * dueño vio que «un día no sumó», y fallaba por los dos lados:
 *
 *   · **Dos panes iguales pasados seguido** —en menos de dos segundos— contaban
 *     uno. El segundo se tragaba sin aviso.
 *   · **Un pan quieto delante de la cámara** más de dos segundos contaba dos, y
 *     tres a los cuatro. Esa ni se veía.
 *
 * Lo que distingue «otro pan» de «el mismo pan que sigue ahí» no es el reloj:
 * es que el código **desaparezca** de la cámara entre uno y otro. Así que se
 * apunta cuándo se VIO por última vez, y el mismo código vuelve a contar solo
 * tras `AUSENCIA_PARA_REPETIR` sin verse.
 *
 * Otro código distinto tiene su propia espera, corta, para que pasar la compra
 * sea rápido sin que un cuadro borroso cuele una lectura a medias.
 */

/**
 * Cuánto tiene que DESAPARECER un código de la cámara para que vuelva a contar.
 *
 * Son cuatro intentos de lectura seguidos sin verlo: un cuadro borroso suelto
 * con el pan quieto delante no llega, y apartar un pan para acercar el
 * siguiente, sí.
 */
export const AUSENCIA_PARA_REPETIR = 800;

/** Cuánto se ignora cualquier OTRO código después de una lectura buena. */
export const ESPERA_CUALQUIERA = 600;

/**
 * Cada cuánto intenta leer la cámara.
 *
 * ⚠️ La librería trae 500 ms de fábrica: con eso un código quieto se ve solo
 * dos veces por segundo, y un cuadro fallido dejaba un hueco de un segundo que
 * se confundía con «lo apartó». Con 200 ms, cinco lecturas por segundo, que el
 * teléfono de una bodega aguanta porque solo se prueban los `FORMATOS`.
 */
export const INTERVALO_DE_LECTURA = 200;

/** Lo último que se aceptó, y cuándo se vio por última vez. */
export type UltimaLectura = { codigo: string; cuando: number; visto: number };

/**
 * Los formatos que se leen, y ninguno más.
 *
 * ⚠️ La lista corta no es por gusto: cuantos menos formatos, menos trabajo por
 * cuadro, y eso en el teléfono de 60 dólares de una bodega es la diferencia
 * entre leer al instante y parecer que se colgó.
 *
 * Están los del comercio —EAN y UPC son los de cualquier producto de estante,
 * CODE_128 y CODE_39 los de las etiquetas que imprime uno mismo, ITF el de las
 * cajas de mayor— y el QR, porque hay negocios que etiquetan así.
 */
export const FORMATOS = [
  "EAN_13",
  "EAN_8",
  "UPC_A",
  "UPC_E",
  "CODE_128",
  "CODE_39",
  "ITF",
  "QR_CODE",
] as const;

export type FormatoDeCodigo = (typeof FORMATOS)[number];

/**
 * ¿Esta lectura vale, o es la misma de hace un instante?
 *
 * Se le pasa lo último que se aceptó y el reloj de ahora. Devuelve `true` solo
 * cuando de verdad hay algo nuevo que hacer.
 */
export function valeLaLectura(
  codigo: string,
  ultimo: UltimaLectura | null,
  ahora: number,
): boolean {
  if (!codigo.trim()) return false;
  if (!ultimo) return true;

  // El mismo: solo si dejó de verse un rato. Ver el encabezado.
  if (codigo === ultimo.codigo) return ahora - ultimo.visto >= AUSENCIA_PARA_REPETIR;
  return ahora - ultimo.cuando >= ESPERA_CUALQUIERA;
}

/**
 * Lo que queda apuntado después de ver un código, valga o no.
 *
 * ⚠️ Aunque no valga, si es el mismo que ya se aceptó se apunta que SIGUE
 * delante: es lo que impide que un pan quieto vuelva a contar.
 */
export function trasVer(
  codigo: string,
  ultimo: UltimaLectura | null,
  ahora: number,
): UltimaLectura | null {
  if (valeLaLectura(codigo, ultimo, ahora)) return { codigo, cuando: ahora, visto: ahora };
  if (ultimo && codigo === ultimo.codigo) return { ...ultimo, visto: ahora };
  return ultimo;
}

/*
 * ═══════════════════════════════════════════════════════════════════════════════
 *  ⚠️ ANDROID LEÍA PEOR QUE IPHONE CON EL MISMO LECTOR (2026-11-04)
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * Prueba del dueño con un cliente: 3 Android y 3 iPhone, 3 códigos buenos. Los
 * iPhone leían de una; a los Android les costaba. Como el lector era el mismo
 * en los dos, la diferencia estaba en la IMAGEN, y medido con fotos simuladas
 * lo que tumba la lectura es lo borroso mucho más que el reflejo: nítida con
 * reflejo se lee entre 81 y 98 % de las veces; muy borrosa, sin reflejo, 1 %.
 *
 * Y la imagen sale borrosa por cómo se usa: la mirilla ocupa casi todo el ancho,
 * así que para llenarla con un código de 4 cm hay que poner el teléfono a unos
 * 6 cm, más cerca de lo que enfoca la mayoría. El iPhone lo disimula mejor.
 *
 * Lo que se hace en Android (`lib/camaraDeLectura.ts`), y el iPhone no se toca:
 *
 *   · **Zoom a 2×**: para llenar la mirilla hay que alejarse a unos 11 cm, donde
 *     sí enfoca.
 *   · **El lector del propio Android** (`BarcodeDetector`), mucho mejor con lo
 *     borroso, con el lector de siempre detrás como red por si falla.
 *   · **La lente que enfoca**: algunos abren la gran angular de enfoque fijo.
 */

/** ¿Es Android? Solo ahí se usa el camino de `lib/camaraDeLectura.ts`. */
export function esAndroid(ua: string = navigator.userAgent): boolean {
  return /Android/i.test(ua);
}

/** Los `FORMATOS` con el nombre que les da el lector del navegador. */
const FORMATOS_NATIVOS: Record<FormatoDeCodigo, string> = {
  EAN_13: "ean_13",
  EAN_8: "ean_8",
  UPC_A: "upc_a",
  UPC_E: "upc_e",
  CODE_128: "code_128",
  CODE_39: "code_39",
  ITF: "itf",
  QR_CODE: "qr_code",
};

/**
 * De lo que este aparato sabe leer, lo que le interesa a un comercio.
 *
 * ⚠️ Vacío si no sabe EAN-13: hay teléfonos sin los servicios de Google que
 * dicen tener el lector y contestan una lista vacía. Sin el formato de todo
 * producto de estante, ese lector no sirve y se queda el de siempre.
 */
export function formatosNativos(soportados: readonly string[]): string[] {
  if (!soportados.includes(FORMATOS_NATIVOS.EAN_13)) return [];
  return Object.values(FORMATOS_NATIVOS).filter((f) => soportados.includes(f));
}

/**
 * Los formatos que NO traen dígito de control.
 *
 * En esos, una barra mal vista da otro código sin que nada lo note: con un EAN
 * borroso, el lector puede «ver» un ITF de seis cifras que no existe. Por eso
 * piden dos lecturas iguales (`confirmarLectura`).
 */
const SIN_DIGITO_DE_CONTROL: ReadonlySet<string> = new Set([FORMATOS_NATIVOS.CODE_39, FORMATOS_NATIVOS.ITF]);

export function tieneDigitoDeControl(formatoNativo: string): boolean {
  return !SIN_DIGITO_DE_CONTROL.has(formatoNativo);
}

/** Cuánto puede pasar entre las dos lecturas iguales para que se confirmen. */
export const VENTANA_DE_CONFIRMACION = 1_500;

export type Candidato = { codigo: string; cuando: number };

/**
 * ¿Ya se vio dos veces seguidas este código?
 *
 * ⚠️ Leer de forma más permisiva tiene un precio: en las fotos simuladas, los
 * ajustes que más leen dieron de vez en cuando un código EQUIVOCADO (3 de cada
 * 120 con la imagen borrosa). En una caja eso es otro producto en la cuenta, y
 * nadie lo ve. Dos lecturas iguales seguidas lo evitan, porque el error no se
 * repite igual en dos cuadros, y solo cuesta una fracción de segundo.
 */
export function confirmarLectura(
  codigo: string,
  candidato: Candidato | null,
  ahora: number,
): { confirmada: boolean; candidato: Candidato } {
  const confirmada =
    candidato !== null && candidato.codigo === codigo && ahora - candidato.cuando <= VENTANA_DE_CONFIRMACION;
  return { confirmada, candidato: { codigo, cuando: ahora } };
}

/** Un trozo del cuadro de la cámara, en sus propios píxeles. */
export type Zona = { x: number; y: number; w: number; h: number };

type Rectangulo = { left: number; top: number; width: number; height: number };

/** Lo que se añade a la mirilla al recortar para el lector: el código no entra justo. */
export const MARGEN_AL_RECORTAR = 0.15;

/** Hasta dónde se acepta un código fuera de la mirilla. Ver `elegirDetectado`. */
export const MARGEN_AL_ELEGIR = 0.5;

/**
 * Qué parte del cuadro de la cámara cae dentro de la mirilla de la pantalla.
 *
 * El vídeo se ve con `object-cover`: se agranda hasta cubrir su caja y lo que
 * sobra se recorta por los lados, así que un punto de la pantalla no es el mismo
 * punto del cuadro.
 *
 * `margen` es la parte del tamaño de la mirilla que se añade a cada lado.
 */
export function zonaDeLaMirilla(
  cuadro: { ancho: number; alto: number },
  caja: Rectangulo,
  mirilla: Rectangulo | null,
  margen: number,
): Zona {
  const todo: Zona = { x: 0, y: 0, w: cuadro.ancho, h: cuadro.alto };
  if (!mirilla || !caja.width || !caja.height || !mirilla.width || !mirilla.height) return todo;

  const escala = Math.max(caja.width / cuadro.ancho, caja.height / cuadro.alto);
  const sobraX = (caja.width - cuadro.ancho * escala) / 2;
  const sobraY = (caja.height - cuadro.alto * escala) / 2;

  const w = mirilla.width / escala;
  const h = mirilla.height / escala;
  const x = (mirilla.left - caja.left - sobraX) / escala - w * margen;
  const y = (mirilla.top - caja.top - sobraY) / escala - h * margen;

  const desdeX = Math.max(0, x);
  const desdeY = Math.max(0, y);
  const hastaX = Math.min(cuadro.ancho, x + w * (1 + 2 * margen));
  const hastaY = Math.min(cuadro.alto, y + h * (1 + 2 * margen));
  if (hastaX <= desdeX || hastaY <= desdeY) return todo;

  return { x: desdeX, y: desdeY, w: hastaX - desdeX, h: hastaY - desdeY };
}

/**
 * De los códigos que vio el lector de Android, el que se está apuntando.
 *
 * Ese lector mira la foto entera y encuentra también los de los productos de al
 * lado. Sin esto, apuntar a la harina del estante podía cobrar el arroz de abajo.
 *
 * ⚠️ `zona` es la mirilla con `MARGEN_AL_ELEGIR`, ancha a propósito: lo único
 * que se descarta es lo que claramente está en otra parte. Si por un aparato
 * raro las posiciones llegaran un poco movidas, una zona justa rechazaría
 * lecturas buenas sin que nadie lo notara, que es justo el fallo que se arregla.
 */
export function elegirDetectado<T extends { boundingBox: { x: number; y: number; width: number; height: number } }>(
  detectados: readonly T[],
  zona: Zona,
): T | null {
  const medioX = zona.x + zona.w / 2;
  const medioY = zona.y + zona.h / 2;
  let mejor: T | null = null;
  let menor = Infinity;

  for (const d of detectados) {
    const x = d.boundingBox.x + d.boundingBox.width / 2;
    const y = d.boundingBox.y + d.boundingBox.height / 2;
    if (x < zona.x || x > zona.x + zona.w || y < zona.y || y > zona.y + zona.h) continue;
    const distancia = (x - medioX) ** 2 + (y - medioY) ** 2;
    if (distancia < menor) {
      menor = distancia;
      mejor = d;
    }
  }
  return mejor;
}

/** Lo que la cámara dice que sabe hacer, en lo que aquí importa. */
export type Capacidades = {
  focusMode?: string[];
  exposureMode?: string[];
  facingMode?: string[];
  zoom?: { min?: number; max?: number; step?: number };
  torch?: boolean;
};

/**
 * Con cuánto zoom se lee. Ver el encabezado de esta parte.
 *
 * El zoom de 2× lleva a unos 11 cm para llenar la mirilla con un código normal,
 * donde enfoca casi cualquier teléfono. Más zoom obligaría a separarse demasiado
 * con un código grande, y el pulso se notaría más.
 */
export const ZOOM_PARA_LEER = 2;

export function zoomParaLeer(zoom: Capacidades["zoom"]): number | null {
  // Con menos de 1,5× de margen no compensa: la persona no notaría la diferencia.
  if (!zoom?.max || zoom.max < 1.5) return null;
  const objetivo = Math.min(ZOOM_PARA_LEER, zoom.max);
  const paso = zoom.step && zoom.step > 0 ? zoom.step : 0;
  const ajustado = paso ? Math.round(objetivo / paso) * paso : objetivo;
  return Math.max(zoom.min ?? 0, Math.min(ajustado, zoom.max));
}

/**
 * Lo que se le pide a la cámara para leer, solo lo que dice que puede hacer.
 *
 * El punto central es el que se usa para enfocar y para calcular la luz: sin él
 * la cámara mide la escena entera, y un código con la luz del local reflejada
 * sale quemado porque el fondo oscuro le pedía más exposición.
 */
export function ajustesParaLeer(c: Capacidades): Record<string, unknown>[] {
  const ajustes: Record<string, unknown>[] = [];
  if (c.focusMode?.includes("continuous")) ajustes.push({ focusMode: "continuous" });
  if (c.exposureMode?.includes("continuous")) ajustes.push({ exposureMode: "continuous" });
  if (c.focusMode?.length) ajustes.push({ pointsOfInterest: [{ x: 0.5, y: 0.5 }] });
  const zoom = zoomParaLeer(c.zoom);
  if (zoom !== null) ajustes.push({ zoom });
  return ajustes;
}

/**
 * ¿Esta lente enfoca sola?
 *
 * `null` es «no se sabe»: el navegador todavía no ha dicho nada de la cámara, y
 * eso no es razón para cambiar de lente. `false` solo cuando contestó sobre lo
 * demás y sin enfoque automático: suele ser la gran angular, que no enfoca de
 * cerca y con la que un código no se lee nunca.
 */
export function enfocaSola(c: Capacidades): boolean | null {
  if (c.focusMode?.length) return c.focusMode.includes("continuous");
  const contesto = c.zoom !== undefined || c.torch !== undefined || c.exposureMode !== undefined;
  return contesto ? false : null;
}

/**
 * Limpia lo que devolvió la cámara: **solo los bordes**.
 *
 * ═══════════════════════════════════════════════════════════════════════════════
 *  ⚠️ POR QUÉ NO SE QUITAN LOS ESPACIOS NI LOS GUIONES DE DENTRO
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * La primera versión los quitaba todos, y estaba mal. Lo destapó su propia
 * prueba, y el motivo es que **hay formatos donde el guion es parte del dato**:
 *
 *   · EAN y UPC —los de cualquier producto de estante— son solo dígitos, así
 *     que ahí daba igual.
 *   · Pero CODE_39 y CODE_128 son los de las etiquetas que imprime el propio
 *     negocio, y ahí caben letras, guiones y espacios. Un SKU «CAM-M-NEG»
 *     quedaba convertido en «CAMMNEG» y no encontraba nada.
 *
 * Y el fallo no se veía: el producto «no aparece», así que alguien lo carga otra
 * vez y el inventario acaba con dos.
 *
 * Recortar los bordes sí hace falta: según el formato llega con un salto de
 * línea detrás, y un código con un carácter invisible al final tampoco encuentra
 * nada, con la misma consecuencia.
 */
export function limpiarCodigo(crudo: string): string {
  return crudo.trim();
}

/**
 * Qué decirle a la persona cuando la cámara no arranca.
 *
 * ═══════════════════════════════════════════════════════════════════════════════
 *  ⚠️ EL MENSAJE TIENE QUE DECIR QUÉ HACER, NO QUÉ FALLÓ
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * «NotAllowedError» no le sirve a nadie. Los tres casos que pasan de verdad
 * tienen los tres una salida distinta, y esa salida es lo único que importa:
 *
 *   · **No dio permiso** (o lo negó antes sin darse cuenta) → hay que dárselo
 *     en el candado de la barra de direcciones. Es el más común con diferencia y
 *     el que más gente da por perdido, porque el navegador no vuelve a
 *     preguntar.
 *   · **La cámara está ocupada** por otra aplicación — la videollamada, otra
 *     pestaña de Softfi abierta.
 *   · **No hay cámara**, que en una computadora de escritorio es lo normal.
 *
 * Y hay un cuarto que no es un fallo del aparato: **sin candado no hay cámara**.
 * Los navegadores solo la dan por HTTPS. Softfi va por HTTPS, así que esto solo
 * salta abriendo por la dirección de la red local.
 */
export function porQueNoAbrio(error: unknown): string {
  const nombre =
    error && typeof error === "object" && "name" in error ? String(error.name) : "";

  if (nombre === "NotAllowedError" || nombre === "SecurityError") {
    return "No diste permiso para la cámara. Tócale al candado de la barra de arriba y permite la cámara para Softfi.";
  }
  if (nombre === "NotFoundError" || nombre === "OverconstrainedError") {
    return "Este aparato no tiene cámara. Escribe el código a mano o usa un lector.";
  }
  if (nombre === "NotReadableError" || nombre === "AbortError") {
    return "La cámara la está usando otra aplicación. Ciérrala y vuelve a intentar.";
  }
  return "No se pudo abrir la cámara. Escribe el código a mano mientras tanto.";
}

/** ¿Este navegador puede dar la cámara? */
export function hayCamara(): boolean {
  return Boolean(navigator.mediaDevices?.getUserMedia);
}

/**
 * ¿Aquí se escanea con la cámara? Solo en el teléfono (y en la tablet).
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  ⚠️ «PUEDE DAR CÁMARA» NO ES «AQUÍ SE ESCANEA CON LA CÁMARA»
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * El botón de la cámara salía también en la computadora, porque `hayCamara`
 * solo pregunta si el navegador SABE pedir cámara — y en una PC la respuesta es
 * sí, tenga webcam o no. Lo vio el dueño en su pantalla: una cámara en cada
 * buscador del mostrador, donde hay un lector láser o se teclea.
 *
 * Y aunque la PC tenga webcam, no sirve para esto: apunta a la cara de quien
 * está sentado, no al producto. Escanear con la cámara es cosa del teléfono,
 * que se lleva a la estantería o se apunta al código con la mano.
 *
 * La pregunta de verdad es si el puntero PRINCIPAL es un dedo (`pointer:
 * coarse`): teléfono o tablet. Una portátil con pantalla táctil sigue teniendo
 * el ratón como principal, y se queda sin cámara, como la PC.
 */
export function seEscaneaConLaCamara(): boolean {
  if (!hayCamara()) return false;
  try {
    return window.matchMedia("(pointer: coarse)").matches;
  } catch {
    return false;
  }
}

/**
 * ¿Lleva botón de escáner la casilla? Casi siempre sí (2026-11-02).
 *
 * En el teléfono y la tablet es la cámara; en la PC, el TELÉFONO unido a esa
 * PC por un QR (ver `lib/telefonoDeLaPc.ts`). Solo se queda sin botón la
 * tablet sin cámara.
 *
 * ⚠️ Es lo que tienen que preguntar los buscadores para dejarle sitio al botón
 * dentro de la casilla —no `seEscaneaConLaCamara`, que en la PC dice que no y
 * dejaría el teléfono encima del texto—.
 */
export function hayBotonDeEscaner(): boolean {
  if (seEscaneaConLaCamara()) return true;
  try {
    return !window.matchMedia("(pointer: coarse)").matches;
  } catch {
    return true;
  }
}
